import * as React from 'react';
import { Box, CircularProgress, IconButton } from '@mui/material';
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MainInput } from '../app/components/Styles';
import { addInbounds, getProductsSimplified } from '../app/store/actions/dataActions';
import { Close, Delete } from '@mui/icons-material';
import { useEffect } from 'react';
import ReactFileReader from 'react-file-reader';
import { notification } from 'antd';
import download from 'downloadjs';
import ExcelJS from 'exceljs';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import Hoverable from '../app/glass/Hoverable';
import { cardShell, fieldStyle, redCta } from '../app/glass/styles';

/** Stock tab stores paginated `{ docs }`; this page needs a flat product array. */
const normalizeProductList = (products) => {
    if (Array.isArray(products)) return products;
    if (Array.isArray(products?.docs)) return products.docs;
    if (Array.isArray(products?.products)) return products.products;
    return [];
};

const sortProductsBySku = (productList) => {
    return [...normalizeProductList(productList)].sort((a, b) => {
        const skuA = (a?.sku || a?.externalId || '').toString().toLowerCase();
        const skuB = (b?.sku || b?.externalId || '').toString().toLowerCase();
        return skuA.localeCompare(skuB);
    });
};

const productMetaLabel = (product) => {
    return [product?.size, product?.color, product?.weight]
        .filter((v) => v != null && v !== '')
        .map((v) => ` (${String(v)})`)
        .join('');
};

export const CreateInbound = ({ addInbounds, getProductsSimplified, products, user, isLoading, isWaiting }) => {
    const { t } = useTranslation();

    const navigate = useNavigate();
    const hasSimplifiedList = Array.isArray(products);
    const productsSortedBySku = React.useMemo(
        () => (hasSimplifiedList ? sortProductsBySku(products) : []),
        [products, hasSimplifiedList]
    );
    const [stockItems, setStockItems] = React.useState([]);
    const [checkedProductIds, setCheckedProductIds] = React.useState([]);
    const [productSearch, setProductSearch] = React.useState('');
    const [file, setFile] = React.useState(null);
    const [isDownloading, setIsDownloading] = React.useState(false);
    const [isRTO, setIsRTO] = React.useState(false);

    const handleFiles = (files) => {
        setFile(files[0]);
    };

    useEffect(() => {
        var reader = new FileReader();
        if (file) {
            reader.onload = async (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];
                    const parsedData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                    const header = parsedData[0];
                    let items = [];
                    parsedData.slice(1).map((row) => {
                        const item = {};
                        row.forEach((value, index) => {
                            item[header[index]] = value;
                        });
                        const catalog = normalizeProductList(products);
                        let found = catalog.find(product => product.sku === item["Product SKU"] || product.externalId === item["Product SKU"]);
                        if (found && item["Quantity"] > 0) {
                            items.push({
                                _id: found._id,
                                sku: found.sku || found.externalId,
                                name: found.name,
                                quantity: item["Quantity"],
                                isRTO: item["Is RTO"] === true || item["Is RTO"] === "TRUE" || item["Is RTO"] === "true" || item["Is RTO"] === 1 || item["Is RTO"] === "1"
                            });
                        }
                    });
                    setStockItems(items);
                } catch (err) {
                    console.log(err);
                }
            };
            reader.readAsArrayBuffer(file);
        }
    }, [file]);

    const handleExportToExcel = async () => {
        setIsDownloading(true)
        notification.info({ message: t("inventory.downloadingInboundExport") })
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Inbound Products');
        workbook.creator = 'Chainz';

        worksheet.columns = [
            { header: 'Product SKU', key: 'sku' },
            { header: 'Product Name', key: 'name' },
            { header: 'Quantity', key: 'quantity' },
            { header: 'Is RTO', key: 'isRTO' },
        ];

        productsSortedBySku.forEach((product) => {
            worksheet.addRow({
                "sku": product.sku || product.externalId,
                "name": product.name,
                "quantity": 0,
                "isRTO": false,
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const mimeType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        const fileName = `${user?.vendor.brandName} Inbound - ${moment().format("DD_MM_YYYY")}.xlsx`
        const blob = new Blob([buffer], { type: mimeType })
        download(blob, fileName, mimeType);
        setIsDownloading(false)
    }

    const handleSubmit = async (event) => {
        event.preventDefault();
        var order = {
            products: stockItems,
            isRTO: isRTO
        }
        addInbounds(order, navigate);
    };

    const availableProducts = React.useMemo(
        () => productsSortedBySku.filter((product) => !stockItems.some((item) => item._id === product._id)),
        [productsSortedBySku, stockItems]
    );

    const filteredAvailableProducts = React.useMemo(() => {
        const term = productSearch.trim().toLowerCase();
        if (!term) {
            return availableProducts;
        }
        return availableProducts.filter((product) => {
            const sku = (product.sku || product.externalId || "").toLowerCase();
            const name = (product.name || "").toLowerCase();
            return sku.includes(term) || name.includes(term);
        });
    }, [availableProducts, productSearch]);

    const toggleProductSelection = (productId) => {
        setCheckedProductIds((prev) => (
            prev.includes(productId)
                ? prev.filter((id) => id !== productId)
                : [...prev, productId]
        ));
    };

    const bulkAddItems = (productsToAdd) => {
        if (!productsToAdd?.length) return;
        setStockItems((prevItems) => {
            const nextItems = [...prevItems];
            productsToAdd.forEach((value) => {
                if (!value || nextItems.some((item) => item._id === value._id)) {
                    return;
                }
                nextItems.push({
                    _id: value._id,
                    sku: value.sku || value.externalId,
                    name: value.name,
                    size: value.size,
                    color: value.color,
                    weight: value.weight,
                    quantity: 1,
                    isRTO: isRTO,
                });
            });
            return nextItems;
        });
        setCheckedProductIds([]);
    };

    const handleAddSelectedProducts = () => {
        const productsToAdd = availableProducts.filter((product) => checkedProductIds.includes(product._id));
        bulkAddItems(productsToAdd);
    };

    const editItem = (idx, { target }) => {
        setStockItems(prevItems => {
            let newItems = [...prevItems];
            newItems[idx].quantity = target.value;
            return newItems;
        });
    };

    const removeItem = (id) => {
        let currItems = stockItems.filter((item) => item._id !== id)
        setStockItems(currItems)
    }

    useEffect(() => {
        getProductsSimplified({})
    }, [])

    const selectedCount = checkedProductIds.length;
    const addColor = selectedCount > 0 ? 'var(--tx)' : 'var(--mut)';
    const addBg = selectedCount > 0 ? 'var(--s08)' : 'var(--s03)';
    const rtoTrack = isRTO ? 'linear-gradient(90deg, #7D010C, #c0303e)' : 'var(--s08)';
    const rtoKnob = isRTO ? '16px' : '0px';
    const canSend = stockItems.length > 0 && !isWaiting;

    if (isLoading || !hasSimplifiedList) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 280 }}>
                <CircularProgress sx={{ color: 'var(--red-tx)' }} />
            </Box>
        )
    }

    return (
        <form onSubmit={handleSubmit}>
            <div className="cz-g1" style={{ display: 'grid', gridTemplateColumns: '1fr 1.3fr', gap: 20, alignItems: 'start' }}>
                <div style={cardShell}>
                    <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 6 }}>{t('inventory.addViaExcelFile')}</div>
                    <div style={{ fontSize: 13, color: 'var(--mut)', marginBottom: 20 }}>{t('inventory.excelHint')}</div>
                    <ReactFileReader handleFiles={handleFiles} fileTypes={[".xlsx", ".xls"]}>
                        <Hoverable
                            base={{
                                borderRadius: 18,
                                border: '1.5px dashed rgba(190,50,62,0.45)',
                                background: 'rgba(125,1,12,0.12)',
                                padding: '34px 20px',
                                display: 'grid',
                                placeItems: 'center',
                                textAlign: 'center',
                                gap: 10,
                                cursor: 'pointer',
                            }}
                            hover={{ background: 'rgba(125,1,12,0.20)' }}
                        >
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#c0303e" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 16V4" />
                                <path d="m7 9 5-5 5 5" />
                                <path d="M4 20h16" />
                            </svg>
                            <div style={{ fontSize: 14, fontWeight: 600 }}>{t('inventory.uploadXlsx')}</div>
                            <div style={{ fontSize: 12, color: 'var(--mut)' }}>{t('inventory.dragDrop')}</div>
                        </Hoverable>
                    </ReactFileReader>
                    {file && (
                        <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--tx2)' }}>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                            <IconButton size="small" onClick={() => setFile(null)} sx={{ color: 'var(--mut)' }}>
                                <Close fontSize="small" />
                            </IconButton>
                        </div>
                    )}
                    <Hoverable
                        as="button"
                        type="button"
                        onClick={handleExportToExcel}
                        disabled={isDownloading}
                        base={{
                            marginTop: 16,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            fontSize: 13,
                            color: 'var(--tx2)',
                            cursor: 'pointer',
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            fontFamily: 'inherit',
                        }}
                        hover={{ color: 'var(--txs)' }}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 3v12" />
                            <path d="m7 10 5 5 5-5" />
                            <path d="M4 21h16" />
                        </svg>
                        {t('inventory.downloadSample')}
                    </Hoverable>
                </div>

                <div style={cardShell}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6, gap: 12, flexWrap: 'wrap' }}>
                        <div style={{ fontSize: 17, fontWeight: 600 }}>{t('inventory.addManually')}</div>
                        <div
                            onClick={() => setIsRTO((v) => !v)}
                            style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer' }}
                            role="switch"
                            aria-checked={isRTO}
                        >
                            <div style={{ fontSize: 12.5, color: 'var(--tx2)' }}>{t('inventory.rtoToggle')}</div>
                            <div
                                style={{
                                    width: 38,
                                    height: 22,
                                    borderRadius: 999,
                                    padding: 2,
                                    boxSizing: 'border-box',
                                    background: rtoTrack,
                                    border: '1px solid var(--s14)',
                                    transition: 'background .2s',
                                }}
                            >
                                <div
                                    style={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: '50%',
                                        background: 'var(--tx)',
                                        transform: `translateX(${rtoKnob})`,
                                        transition: 'transform .2s',
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--mut)', marginBottom: 18 }}>{t('inventory.manualHint')}</div>

                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            padding: '4px 8px 4px 18px',
                            borderRadius: 999,
                            background: 'var(--s04)',
                            border: '1px solid var(--s10)',
                            color: 'var(--mut)',
                            marginBottom: 14,
                        }}
                    >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                            <circle cx="11" cy="11" r="7" />
                            <path d="m20 20-3.2-3.2" />
                        </svg>
                        <input
                            style={{ ...fieldStyle, border: 'none', background: 'transparent', padding: '10px 8px', width: '100%' }}
                            placeholder={t('inventory.searchProducts')}
                            value={productSearch}
                            onChange={({ target }) => setProductSearch(target.value)}
                        />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto', paddingRight: 4 }}>
                        {availableProducts.length === 0 ? (
                            <div style={{ fontSize: 13, color: 'var(--mut)', padding: '8px 4px' }}>{t('inventory.allProductsAdded')}</div>
                        ) : filteredAvailableProducts.length === 0 ? (
                            <div style={{ fontSize: 13, color: 'var(--mut)', padding: '8px 4px' }}>{t('inventory.noProductsMatchSearch')}</div>
                        ) : (
                            filteredAvailableProducts.map((product) => {
                                const isChecked = checkedProductIds.includes(product._id);
                                return (
                                    <Hoverable
                                        key={product._id}
                                        onClick={() => toggleProductSelection(product._id)}
                                        base={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 12,
                                            padding: '11px 14px',
                                            borderRadius: 14,
                                            background: isChecked ? 'rgba(125,1,12,0.22)' : 'var(--s03)',
                                            border: `1px solid ${isChecked ? 'rgba(190,50,62,0.40)' : 'var(--s06)'}`,
                                            cursor: 'pointer',
                                        }}
                                        hover={{ background: 'var(--s06)' }}
                                    >
                                        <div
                                            style={{
                                                width: 20,
                                                height: 20,
                                                flexShrink: 0,
                                                borderRadius: 7,
                                                display: 'grid',
                                                placeItems: 'center',
                                                background: isChecked ? 'linear-gradient(160deg, #9c0410, #7D010C)' : 'var(--s05)',
                                                border: `1px solid ${isChecked ? 'rgba(190,50,62,0.60)' : 'rgba(255,255,255,0.20)'}`,
                                            }}
                                        >
                                            {isChecked && (
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--red-tx)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="m5 13 4 4 10-10" />
                                                </svg>
                                            )}
                                        </div>
                                        <div style={{ fontSize: 12.5, color: 'var(--mut2)' }}>{product.sku || product.externalId}</div>
                                        <div style={{ fontSize: 13.5, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{product.name}</div>
                                    </Hoverable>
                                );
                            })
                        )}
                    </div>

                    {stockItems.length > 0 && (
                        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {stockItems.map((product, idx) => (
                                <div
                                    key={product._id || idx}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 10,
                                        padding: '8px 12px',
                                        borderRadius: 14,
                                        background: 'var(--s03)',
                                        border: '1px solid var(--s08)',
                                    }}
                                >
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 12.5, color: 'var(--mut2)' }}>{product.sku}</div>
                                        <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {product.name}{productMetaLabel(product)}
                                        </div>
                                    </div>
                                    <MainInput
                                        required
                                        type="number"
                                        inputProps={{ min: 1 }}
                                        value={stockItems[idx].quantity}
                                        onChange={(event) => editItem(idx, event)}
                                        sx={{ width: 88, '& .MuiInputBase-input': { py: 1, textAlign: 'center' } }}
                                    />
                                    <IconButton onClick={() => removeItem(product._id)} size="small" sx={{ color: 'var(--red-tx)' }}>
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </div>
                            ))}
                        </div>
                    )}

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18, flexWrap: 'wrap' }}>
                        <button
                            type="button"
                            disabled={selectedCount === 0}
                            onClick={handleAddSelectedProducts}
                            style={{
                                flex: 1,
                                minWidth: 140,
                                textAlign: 'center',
                                padding: 12,
                                borderRadius: 999,
                                fontSize: 13.5,
                                fontWeight: 600,
                                color: addColor,
                                background: addBg,
                                border: '1px solid var(--s12)',
                                cursor: selectedCount > 0 ? 'pointer' : 'default',
                                fontFamily: 'inherit',
                            }}
                        >
                            {t('inventory.addSelected', { count: selectedCount })}
                        </button>
                        <Hoverable
                            as="button"
                            type="submit"
                            disabled={!canSend}
                            base={{
                                ...redCta,
                                flex: 1,
                                minWidth: 140,
                                justifyContent: 'center',
                                opacity: canSend ? 1 : 0.55,
                                cursor: canSend ? 'pointer' : 'default',
                            }}
                            hover={canSend ? { background: 'linear-gradient(160deg, rgba(156,4,16,0.65), rgba(100,1,10,0.50))' } : {}}
                        >
                            {isWaiting ? <CircularProgress size={18} sx={{ color: 'var(--red-tx)' }} /> : t('inventory.sendInventory')}
                        </Hoverable>
                    </div>
                </div>
            </div>
        </form>
    );
}

const mapStateToProps = (state) => ({
    user: state?.auth?.user,
    isLoading: state?.records?.isLoading,
    isWaiting: state?.wait?.isLoading,
    warehouses: state?.records?.warehouses?.docs,
    products: state?.records?.products
});

const mapDispatchToProps = { addInbounds, getProductsSimplified };

export default connect(mapStateToProps, mapDispatchToProps)(CreateInbound);
